import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { F0Box, F0Button, F0Icon, F0Text, useAiChat } from '@factorialco/f0-react'
import { Check } from '@factorialco/f0-react/icons/app'
import {
  AiExecuteTimeoffPolicyChangeDocument,
  AiExecuteTimeoffPolicyChangeMutation,
  ExecuteTimeoffPolicyChangeAllowanceMappingsInput,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { dispatch, EventTypes } from 'modules/core/lib/pubSub'

/**
 * Args streamed by the agent `previewTimeoffPolicyChange` tool call.
 * Pins the contract between the agent side (emission) and the UI.
 */
export type PreviewAllowance = {
  sourceAllowanceId: string
  sourceAllowanceName: string
  sourceUnit: 'days' | 'hours'
  sourceAvailableBalance: string
  targetAllowanceId: string | null
  targetAllowanceName: string | null
  targetUnit: 'days' | 'hours' | null
  targetAllowanceOptions?: {
    id: string
    name: string
    unit: 'days' | 'hours'
  }[]
}

export type PreviewArgs = {
  employeeId: string
  employeeName: string
  sourcePolicyId: string
  sourcePolicyName: string
  targetPolicyId: string
  targetPolicyName: string
  effectiveAt: string
  allowances: PreviewAllowance[]
}

type Props = {
  args: PreviewArgs
}

/**
 * Internal per-row decision driving the chip UI.
 * - `transfer` -> emitted as a transfer mapping to the backend
 * - `discard`  -> no row emitted to the backend (UI label: "Discard")
 * - `pending`  -> user has not yet picked for an unmatched allowance
 *
 * Note: the backend contract has no `resolution` concept — discards are
 * implicit (absence of a row in `allowanceMappings`). This type only drives
 * the UI; only `transfer` decisions are translated into a mapping payload.
 *
 */
type TransferDecision = 'transfer' | 'discard' | 'pending'
type Phase = 'idle' | 'executing' | 'success' | 'error' | 'cancelled'
type TargetSelection = Record<string, { id: string; name: string; unit: 'days' | 'hours' }>
type TargetAllowanceOption = { id: string; name: string; unit: 'days' | 'hours' }
/** Single rounded card wrapping the comparison table. */
const TableCard: FC<{ children: ReactNode; testId?: string }> = ({ children, testId }) => (
  <F0Box
    data-testid={testId}
    display="flex"
    flexDirection="column"
    border="default"
    borderRadius="md"
    overflow="hidden"
  >
    {children}
  </F0Box>
)

/**
 * A row inside the table card. Mirrors the Figma layout:
 * - Left cell uses muted (`description`) text — both for the header and for
 *   each "Current" allowance name.
 * - Right cell uses default body text for the "New" allowance name.
 * - First row has no top border; subsequent rows get a subtle separator.
 */
const TableRow: FC<{
  left: string
  right: ReactNode
  withTopBorder?: boolean
  header?: boolean
}> = ({ left, right, withTopBorder, header }) => (
  <F0Box
    display="grid"
    columns="2"
    padding="md"
    gap="md"
    borderBottom={header ? 'default' : undefined}
    borderTop={withTopBorder ? 'default' : 'none'}
  >
    <F0Box display="flex" flexDirection="column">
      <F0Text variant={header ? 'label' : 'description'} content={left} />
    </F0Box>
    <F0Box display="flex" flexDirection="column">
      {typeof right === 'string' ? (
        <F0Text variant={header ? 'label' : 'body'} content={right} />
      ) : (
        right
      )}
    </F0Box>
  </F0Box>
)

const buildMappings = (
  allowances: PreviewAllowance[],
  decisions: Record<string, TransferDecision>,
  targetSelections: TargetSelection
): ExecuteTimeoffPolicyChangeAllowanceMappingsInput[] =>
  allowances.flatMap((a) => {
    const stored = decisions[a.sourceAllowanceId]
    if (stored !== 'transfer') return []
    const targetAllowanceId = targetSelections[a.sourceAllowanceId]?.id ?? a.targetAllowanceId
    if (!targetAllowanceId) return []
    return [
      {
        sourceAllowanceId: a.sourceAllowanceId,
        targetAllowanceId,
      },
    ]
  })

const AllowanceTargetSelectionQuestion: FC<{
  sourceAllowanceName: string
  options: TargetAllowanceOption[]
  initialTargetAllowanceId?: string
  onConfirm: (option: TargetAllowanceOption) => void
  onCancel: () => void
}> = ({ sourceAllowanceName, options, initialTargetAllowanceId, onConfirm, onCancel }) => {
  const { setClarifyingQuestion } = useAiChat()
  const [selectedOptionId, setSelectedOptionId] = useState(
    initialTargetAllowanceId ?? options[0]?.id
  )

  useEffect(() => () => setClarifyingQuestion(null), [setClarifyingQuestion])

  useEffect(() => {
    const selectedOption = options.find((option) => option.id === selectedOptionId)

    setClarifyingQuestion({
      currentStep: {
        question: i18n.t('common.ai.policy_change.target_allowance_selection_question', {
          allowance: sourceAllowanceName,
        }),
        options: options.map((option) => ({ id: option.id, label: option.name })),
        selectionMode: 'single',
        optional: false,
        allowCustomAnswer: false,
        selectedOptionIds: selectedOptionId ? [selectedOptionId] : [],
        isCustomAnswerActive: false,
      },
      currentStepIndex: 0,
      totalSteps: 1,
      toggleOption: setSelectedOptionId,
      confirm: () => {
        if (!selectedOption) return
        setClarifyingQuestion(null)
        onConfirm(selectedOption)
      },
      skip: () => {},
      cancel: () => {
        setClarifyingQuestion(null)
        onCancel()
      },
      back: () => {},
      setCustomAnswerText: () => {},
      setCustomAnswerActive: () => {},
      activateCustomAnswer: () => {},
    })
  }, [onCancel, onConfirm, options, selectedOptionId, setClarifyingQuestion, sourceAllowanceName])

  return null
}

const AllowanceUnmatchedDecisionQuestion: FC<{
  sourceAllowanceName: string
  onDiscard: () => void
  onTransferToOther: () => void
}> = ({ sourceAllowanceName, onDiscard, onTransferToOther }) => {
  const { setClarifyingQuestion } = useAiChat()
  const [selectedDecision, setSelectedDecision] = useState<'discard' | 'transfer_to_other'>(
    'discard'
  )

  useEffect(() => () => setClarifyingQuestion(null), [setClarifyingQuestion])

  useEffect(() => {
    setClarifyingQuestion({
      currentStep: {
        question: i18n.t('common.ai.policy_change.unmatched_decision_question', {
          allowance: sourceAllowanceName,
        }),
        options: [
          { id: 'discard', label: i18n.t('common.ai.policy_change.discard') },
          {
            id: 'transfer_to_other',
            label: i18n.t('common.ai.policy_change.transfer_to_other_allowance'),
          },
        ],
        selectionMode: 'single',
        optional: false,
        allowCustomAnswer: false,
        selectedOptionIds: [selectedDecision],
        isCustomAnswerActive: false,
      },
      currentStepIndex: 0,
      totalSteps: 1,
      toggleOption: (optionId: string) => {
        if (optionId === 'discard' || optionId === 'transfer_to_other') {
          setSelectedDecision(optionId)
        }
      },
      confirm: () => {
        setClarifyingQuestion(null)
        if (selectedDecision === 'discard') {
          onDiscard()
        } else {
          onTransferToOther()
        }
      },
      skip: () => {},
      cancel: () => {
        setClarifyingQuestion(null)
        onDiscard()
      },
      back: () => {},
      setCustomAnswerText: () => {},
      setCustomAnswerActive: () => {},
      activateCustomAnswer: () => {},
    })
  }, [onDiscard, onTransferToOther, selectedDecision, setClarifyingQuestion, sourceAllowanceName])

  return null
}

export const TimeoffPolicyChangePreview: FC<Props> = ({ args }) => {
  const initial = useMemo<Record<string, TransferDecision>>(
    () =>
      Object.fromEntries(
        args.allowances.map<[string, TransferDecision]>((a) => [
          a.sourceAllowanceId,
          a.targetAllowanceId ? 'transfer' : 'pending',
        ])
      ),
    [args.allowances]
  )

  const [decisions, setDecisions] = useState<Record<string, TransferDecision>>(initial)
  const [appliedDecisions, setAppliedDecisions] =
    useState<Record<string, TransferDecision>>(initial)
  const [targetSelections, setTargetSelections] = useState<TargetSelection>({})
  const [appliedTargetSelections, setAppliedTargetSelections] = useState<TargetSelection>({})
  const [targetSelectionSourceId, setTargetSelectionSourceId] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [executePolicyChange] = useMutation(AiExecuteTimeoffPolicyChangeDocument, {
    extractorFn: (data: AiExecuteTimeoffPolicyChangeMutation) =>
      data.timeoffMutations.executeTimeoffPolicyChange,
  })

  const setDecision = useCallback(
    (sourceId: string, decision: 'transfer' | 'discard') => {
      setDecisions((prev) => ({ ...prev, [sourceId]: decision }))
      if (decision === 'discard') {
        if (targetSelectionSourceId === sourceId) setTargetSelectionSourceId(null)
        setTargetSelections((prev) => {
          const next = { ...prev }
          delete next[sourceId]
          return next
        })
      }
    },
    [targetSelectionSourceId]
  )

  const isForcedDiscard = (a: PreviewAllowance) =>
    !a.targetAllowanceId && (a.targetAllowanceOptions ?? []).length === 0

  const isAllowanceResolved = (a: PreviewAllowance) => {
    const decision = decisions[a.sourceAllowanceId]
    if (decision === 'discard') return true
    if (decision !== 'transfer') return false
    if (targetSelectionSourceId === a.sourceAllowanceId) return false
    if (a.targetAllowanceId) return true

    return Boolean(targetSelections[a.sourceAllowanceId]?.id)
  }

  const hasMismatch = args.allowances.some((a) => !a.targetAllowanceId)
  const forcedDiscardAllowances = args.allowances.filter(isForcedDiscard)
  const decisionRequiredAllowances = args.allowances.filter(
    (a) => !a.targetAllowanceId && !isForcedDiscard(a)
  )
  const unresolvedUnmatchedAllowances = args.allowances.filter(
    (a) => !a.targetAllowanceId && !isForcedDiscard(a) && !isAllowanceResolved(a)
  )
  const pendingDecisionAllowance = targetSelectionSourceId
    ? undefined
    : unresolvedUnmatchedAllowances[0]
  const interactiveDecisionAllowances = pendingDecisionAllowance ? [pendingDecisionAllowance] : []
  const allResolved = args.allowances.every(isAllowanceResolved)

  useEffect(() => {
    const forcedDiscardIds = args.allowances
      .filter((a) => !a.targetAllowanceId && (a.targetAllowanceOptions ?? []).length === 0)
      .map((a) => a.sourceAllowanceId)

    if (forcedDiscardIds.length === 0) return

    setDecisions((prev) => {
      if (forcedDiscardIds.every((id) => prev[id] === 'discard')) return prev

      return {
        ...prev,
        ...Object.fromEntries(forcedDiscardIds.map((id) => [id, 'discard' as const])),
      }
    })
  }, [args.allowances])

  const apply = async () => {
    if ((phase !== 'idle' && phase !== 'error') || !allResolved) return
    setErrorMessage(null)
    setPhase('executing')

    const allowanceMappings = buildMappings(args.allowances, decisions, targetSelections)
    const variables = {
      targetPolicyId: args.targetPolicyId,
      effectiveAt: args.effectiveAt,
      employeeMappings: [{ employeeId: args.employeeId, allowanceMappings }],
    }
    let handled: ReturnType<typeof handleResult>
    try {
      const result = await executePolicyChange({
        variables,
      })
      handled = handleResult(result, () => {})
    } catch {
      setErrorMessage(i18n.t('errors.generic_error'))
      setPhase('error')
      return
    }

    if (handled.success) {
      dispatch({
        type: EventTypes.ONE_MUTATION_EXECUTED,
        data: {
          operationName: 'ExecuteTimeoffPolicyChange',
          variables,
        },
      })
      setAppliedDecisions(decisions)
      setAppliedTargetSelections(targetSelections)
      setPhase('success')
      return
    }

    const message = handled.errors
      .map((e) => ('message' in e ? e.message : i18n.t('errors.generic_error')))
      .filter(Boolean)
      .join(', ')
    setErrorMessage(message || i18n.t('errors.generic_error'))
    setPhase('error')
  }

  const handleConfirm = () => apply()
  const handleCancel = () => setPhase('cancelled')
  const activeTargetSelectionAllowance = targetSelectionSourceId
    ? args.allowances.find((a) => a.sourceAllowanceId === targetSelectionSourceId)
    : undefined
  const activeTargetSelectionOptions = useMemo(
    () => activeTargetSelectionAllowance?.targetAllowanceOptions ?? [],
    [activeTargetSelectionAllowance?.targetAllowanceOptions]
  )
  const handlePendingDecisionDiscard = useCallback(() => {
    if (!pendingDecisionAllowance) return

    setDecision(pendingDecisionAllowance.sourceAllowanceId, 'discard')
  }, [pendingDecisionAllowance, setDecision])
  const handlePendingDecisionTransferToOther = useCallback(() => {
    if (!pendingDecisionAllowance) return

    setDecision(pendingDecisionAllowance.sourceAllowanceId, 'transfer')
    setTargetSelectionSourceId(pendingDecisionAllowance.sourceAllowanceId)
  }, [pendingDecisionAllowance, setDecision])
  const handleTargetSelectionConfirm = useCallback(
    (option: TargetAllowanceOption) => {
      if (!activeTargetSelectionAllowance) return

      setTargetSelections((prev) => ({
        ...prev,
        [activeTargetSelectionAllowance.sourceAllowanceId]: option,
      }))
      setTargetSelectionSourceId(null)
    },
    [activeTargetSelectionAllowance]
  )
  const handleTargetSelectionCancel = useCallback(() => {
    if (!activeTargetSelectionAllowance) return

    setDecision(activeTargetSelectionAllowance.sourceAllowanceId, 'discard')
    setTargetSelectionSourceId(null)
  }, [activeTargetSelectionAllowance, setDecision])

  const renderRightCell = (
    a: PreviewAllowance,
    visibleDecisions: Record<string, TransferDecision>,
    visibleTargetSelections: TargetSelection
  ): ReactNode => {
    const discardCell = (
      <F0Text
        variant="body"
        content={i18n.t('common.ai.policy_change.discard_row')}
        style={{ color: 'hsl(var(--critical-70))' }}
      />
    )

    if (a.targetAllowanceId) {
      const r = visibleDecisions[a.sourceAllowanceId]
      if (r === 'discard') {
        return discardCell
      }
      return visibleTargetSelections[a.sourceAllowanceId]?.name ?? a.targetAllowanceName ?? ''
    }
    const r = visibleDecisions[a.sourceAllowanceId]
    if (r === 'discard') return discardCell
    if (r === 'transfer')
      return (
        visibleTargetSelections[a.sourceAllowanceId]?.name ??
        i18n.t('common.ai.policy_change.no_match')
      )
    return i18n.t('common.ai.policy_change.no_match')
  }

  const renderPendingAllowanceDescription = () => {
    const allowanceNames = decisionRequiredAllowances.map(
      (allowance) => allowance.sourceAllowanceName
    )
    const allowanceNameParts = new Intl.ListFormat(i18n.locale, {
      style: 'long',
      type: 'conjunction',
    }).formatToParts(allowanceNames)
    const interpolation = {
      allowances: (
        <>
          {allowanceNameParts.map((part, index) =>
            part.type === 'element' ? (
              <F0Text
                key={`${part.value}-${index}`}
                as="span"
                variant="body"
                content={part.value}
                style={{ fontWeight: 600 }}
              />
            ) : (
              part.value
            )
          )}
        </>
      ),
    }

    return (
      <F0Box>
        {decisionRequiredAllowances.length === 1
          ? i18n.tView('common.ai.policy_change.unmatched_pending_list_one', interpolation)
          : i18n.tView('common.ai.policy_change.unmatched_pending_list_other', interpolation)}
      </F0Box>
    )
  }

  const isBusy = phase === 'executing'
  const hasFinalMapping =
    phase !== 'success' &&
    args.allowances.some((a) => {
      const decision = decisions[a.sourceAllowanceId]

      return decision === 'discard' || Boolean(targetSelections[a.sourceAllowanceId])
    })
  const showFinalMapping = (hasFinalMapping && allResolved) || phase === 'success'
  const allPendingDecisionsAreForcedDiscard =
    forcedDiscardAllowances.length > 0 && !pendingDecisionAllowance
  const showActions = allResolved
  const finalMappingDecisions = phase === 'success' ? appliedDecisions : decisions
  const finalMappingTargetSelections =
    phase === 'success' ? appliedTargetSelections : targetSelections
  const previewTableDecisions = phase === 'success' ? appliedDecisions : initial
  const previewTableTargetSelections = phase === 'success' ? appliedTargetSelections : {}

  const renderMappingTable = (
    visibleDecisions: Record<string, TransferDecision>,
    visibleTargetSelections: TargetSelection,
    testId: string
  ) => (
    <F0Box paddingY="sm">
      <TableCard testId={testId}>
        <TableRow
          left={i18n.t('common.ai.policy_change.column_current')}
          right={i18n.t('common.ai.policy_change.column_new')}
          header
        />
        {args.allowances.map((a, index) => (
          <TableRow
            key={a.sourceAllowanceId}
            withTopBorder={index > 0}
            left={a.sourceAllowanceName}
            right={renderRightCell(a, visibleDecisions, visibleTargetSelections)}
          />
        ))}
      </TableCard>
    </F0Box>
  )

  if (phase === 'cancelled') {
    return (
      <F0Box display="flex" flexDirection="column" gap="xs">
        <F0Text variant="body" content={i18n.t('common.ai.policy_change.cancelled_headline')} />
        <F0Text variant="description" content={i18n.t('common.ai.policy_change.cancelled_body')} />
      </F0Box>
    )
  }

  return (
    <F0Box display="flex" flexDirection="column" gap="md">
      <F0Text variant="body" content={i18n.t('common.ai.policy_change.intro')} />

      <F0Box>
        <F0Text
          as="span"
          variant="body"
          content={i18n.t('common.ai.policy_change.moving_summary_prefix', {
            employee: args.employeeName,
          })}
        />{' '}
        <F0Text
          as="span"
          variant="body"
          content={args.targetPolicyName}
          style={{ fontWeight: 600 }}
        />{' '}
        <F0Text
          as="span"
          variant="body"
          content={
            hasMismatch
              ? i18n.t('common.ai.policy_change.mismatch_summary_suffix')
              : i18n.t('common.ai.policy_change.moving_summary_suffix')
          }
        />
      </F0Box>

      {phase !== 'success' &&
        !allPendingDecisionsAreForcedDiscard &&
        renderMappingTable(
          previewTableDecisions,
          previewTableTargetSelections,
          'policy-change-table'
        )}

      {phase !== 'success' &&
        !allPendingDecisionsAreForcedDiscard &&
        decisionRequiredAllowances.length > 0 &&
        renderPendingAllowanceDescription()}

      {showFinalMapping && (
        <F0Box display="flex" flexDirection="column" gap="sm">
          {phase !== 'success' &&
            allResolved &&
            !allPendingDecisionsAreForcedDiscard &&
            hasMismatch && (
              <F0Text variant="body" content={i18n.t('common.ai.policy_change.final_mapping')} />
            )}
          {renderMappingTable(
            finalMappingDecisions,
            finalMappingTargetSelections,
            'policy-change-table'
          )}
        </F0Box>
      )}

      {phase !== 'success' &&
        forcedDiscardAllowances.map((a) => (
          <F0Box key={a.sourceAllowanceId} display="flex" flexDirection="column" gap="sm">
            <F0Box>
              <F0Text
                as="span"
                variant="body"
                content={a.sourceAllowanceName}
                style={{ fontWeight: 600 }}
              />{' '}
              <F0Text
                as="span"
                variant="body"
                content={i18n.t('common.ai.policy_change.forced_discard_explanation')}
              />
            </F0Box>
          </F0Box>
        ))}

      {interactiveDecisionAllowances.map((a) => (
        <F0Box key={a.sourceAllowanceId} display="flex" flexDirection="column" gap="sm">
          <AllowanceUnmatchedDecisionQuestion
            sourceAllowanceName={a.sourceAllowanceName}
            onDiscard={handlePendingDecisionDiscard}
            onTransferToOther={handlePendingDecisionTransferToOther}
          />
        </F0Box>
      ))}

      {activeTargetSelectionAllowance && (
        <AllowanceTargetSelectionQuestion
          sourceAllowanceName={activeTargetSelectionAllowance.sourceAllowanceName}
          options={activeTargetSelectionOptions}
          initialTargetAllowanceId={
            targetSelections[activeTargetSelectionAllowance.sourceAllowanceId]?.id
          }
          onConfirm={handleTargetSelectionConfirm}
          onCancel={handleTargetSelectionCancel}
        />
      )}

      {phase === 'error' && errorMessage && (
        <F0Box paddingTop="md">
          <F0Text
            variant="body"
            content={i18n.t('common.ai.policy_change.error', { message: errorMessage })}
          />
        </F0Box>
      )}

      {phase === 'success' ? (
        <F0Box display="flex" flexDirection="column" gap="xs" paddingTop="md">
          <F0Box display="flex" flexDirection="row" gap="sm" alignItems="start">
            <F0Icon icon={Check} size="md" color="default" />
            <F0Text
              variant="body"
              content={i18n.t('common.ai.policy_change.success', {
                date: args.effectiveAt,
              })}
            />
          </F0Box>
        </F0Box>
      ) : showActions ? (
        <F0Box display="flex" flexDirection="row" justifyContent="start" gap="md" paddingTop="md">
          <F0Button
            variant="outline"
            onClick={handleCancel}
            disabled={isBusy}
            label={i18n.t('common.ai.policy_change.cancel')}
          />
          <F0Button
            variant="default"
            onClick={handleConfirm}
            disabled={!allResolved || isBusy}
            label={i18n.t('common.ai.policy_change.confirm_changes')}
          />
        </F0Box>
      ) : null}
    </F0Box>
  )
}
